import { ref, onMounted, onUnmounted, watch, markRaw } from "vue";
import NykButton from "../NykButton/NykButton.vue"

export default {
    name: "BlackthornEvent",
    components: {
        NykButton
    },
    props: {
        requireConsent: {
            type: Boolean,
            default: true
        },
        eventUrl: {
            type: String,
            default: null
        },
        labels: {
            type: Object,
            default: () => ({})
        },
        ariaLabels: {
            type: Object,
            default: () => ({})
        }
    },
    setup(props) {
        const cookieAccepted = ref(false);
        const isAppReady = ref(false);
        const eventsApp = ref(null);
        const statusMessage = ref('');
        const containerRef = ref(null);
        const routeChangeCount = ref(0);
        const isInitialized = ref(false);

        // Check cookie consent on mount
        const checkCookieConsent = () => {
            if (!props.requireConsent) {
                cookieAccepted.value = true;
                return;
            }

            cookieAccepted.value = window.CookieInformation?.getConsentGivenFor("cookie_cat_functional") || false;
        };

        // Handle cookie consent changes
        const handleCookieConsentChange = () => {
            checkCookieConsent();
        };

        // Re-open cookie consent modal
        const acceptCookieConsent = () => {
            if (window.CookieConsent && window.CookieConsent.renew) {
                window.CookieConsent.renew();
            }
        };

        // Load Blackthorn embed.js script
        const loadBlackthornScript = () => {
            return new Promise((resolve, reject) => {
                // Check if script already loaded
                if (window.EventsApp) {
                    resolve();
                    return;
                }

                const script = document.createElement("script");
                script.src = "https://events.nykredit.dk/embed.js";
                script.async = true;
                script.onload = () => resolve();
                script.onerror = () => reject(new Error("Failed to load Blackthorn embed.js"));
                document.head.appendChild(script);
            });
        };

        // Push event to dataLayer for tracking
        const pushToDataLayer = (eventName, eventData) => {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: eventName,
                blackthornEvent: eventData
            });
        };

        // Initialize Blackthorn EventsApp
        const initializeBlackthornApp = async () => {
            if (!cookieAccepted.value || eventsApp.value || isInitialized.value) {
                return;
            }

            // Set flag immediately to prevent race conditions
            isInitialized.value = true;

            try {
                statusMessage.value = props.ariaLabels.loadingStatus;
                await loadBlackthornScript();

                if (!props.eventUrl) {
                    console.warn("BlackthornEvent: No eventUrl provided. Embed will not be shown.");
                    // Reset flag so initialization can be retried if configuration changes
                    isInitialized.value = false;
                    return;
                }

                // Parse the URL to extract organization ID and path
                // eventUrl format: https://events.nykredit.dk/{orgId}/{path}?brand={brandSlug}
                const url = new URL(props.eventUrl);
                const pathSegments = url.pathname.split('/').filter(segment => segment);

                if (pathSegments.length < 2) {
                    console.warn("BlackthornEvent: Invalid eventUrl format. Embed will not be shown.");
                    // Reset flag so initialization can be retried if configuration changes
                    isInitialized.value = false;
                    return;
                }

                // First segment is organizationId, rest is the path
                const organizationId = pathSegments[0];
                const path = pathSegments.slice(1).join('/');

                // Use the path with query params as-is (brand param already included from backend)
                const pathWithParams = url.search ? `${path}${url.search}` : path;

                // Initialize EventsApp with listeners (wrapped in markRaw to prevent reactivity on cross-origin iframe)
                eventsApp.value = markRaw(new window.EventsApp({
                    orgId: organizationId,
                    path: pathWithParams,
                    width: "100%",
                    height: "100%",
                    scrolling: 'no',
                    listeners: [
                        {
                            // Track when app is ready
                            event: "APP_READY",
                            handler: (params) => {
                                isAppReady.value = true;
                                statusMessage.value = '';
                                pushToDataLayer("blackthorn_app_ready", {
                                    organizationId: organizationId,
                                    path: path
                                });
                            }
                        },
                        {
                            event: "CONTENT_SIZE_CHANGED",
                            handler: (params) => {
                                if (params && typeof params.height === "number" && containerRef.value) {
                                    setTimeout(() => {
                                        const frameHeight = params.height;
                                        const frameContainer = containerRef.value;
                                        frameContainer.style.height = frameHeight + "px";

                                        if (frameContainer) {
                                            frameContainer.style.minHeight = frameHeight + "px";
                                            frameContainer.style.height = frameHeight + "px";
                                        }
                                    });
                                }
                            }
                        },
                        {
                            // Scroll to top on route change (after initial load)
                            event: "ROUTE_CHANGED",
                            handler: (params) => {
                                routeChangeCount.value++;

                                // Skip first 2 route changes (initial load redirects)
                                if (routeChangeCount.value <= 2) {
                                    return;
                                }

                                // Track route change
                                pushToDataLayer("blackthorn_route_changed", {
                                    path: params.path || "unknown"
                                });

                                // Scroll to top of container
                                if (containerRef.value) {
                                    const containerTop = containerRef.value.getBoundingClientRect().top + window.scrollY;
                                    window.scrollTo({ top: containerTop, behavior: "smooth" });
                                }
                            }
                        },
                        {
                            // Track form submissions (registrations)
                            event: "FORM_SUBMITTED",
                            handler: (params) => {
                                pushToDataLayer("blackthorn_form_submitted", {
                                    formName: params.name || "unknown",
                                    formPath: params.path || "unknown",
                                    formData: params.data || {}
                                });

                                // Also push legacy format for backwards compatibility
                                window.dataLayer = window.dataLayer || [];
                                window.dataLayer.push({
                                    event: "formSubmission",
                                    formName: params.name,
                                    formPath: params.path
                                });
                            }
                        }
                    ]
                }));

                // Mount the EventsApp to the container
                eventsApp.value.mount(".nyk-blackthorn-event__container");

            } catch (error) {
                console.error("BlackthornEvent: Failed to initialize EventsApp", error);
                statusMessage.value = '';
                // Reset flag on error so user can retry
                isInitialized.value = false;
            }
        };

        // Watch for cookie consent changes
        watch(cookieAccepted, (newValue, oldValue) => {
            if (newValue && newValue !== oldValue) {
                // User accepted cookies - initialize app
                initializeBlackthornApp();
            } else if (!newValue && oldValue) {
                // User revoked cookies - cleanup and reset
                if (eventsApp.value && eventsApp.value.destroy) {
                    eventsApp.value.destroy();
                    eventsApp.value = null;
                }
                isInitialized.value = false;
                isAppReady.value = false;
            }
        });

        // Setup on mount
        onMounted(() => {
            checkCookieConsent();

            // Listen for cookie consent changes
            window.addEventListener(
                "CookieInformationConsentGiven",
                handleCookieConsentChange,
                false
            );

            // Initialize if consent already given
            if (cookieAccepted.value) {
                initializeBlackthornApp();
            }
        });

        // Cleanup on unmount
        onUnmounted(() => {
            window.removeEventListener(
                "CookieInformationConsentGiven",
                handleCookieConsentChange
            );

            // Cleanup EventsApp if it exists
            if (eventsApp.value && eventsApp.value.destroy) {
                eventsApp.value.destroy();
            }
        });

        return {
            cookieAccepted,
            isAppReady,
            containerRef,
            statusMessage,
            acceptCookieConsent
        };
    }
};
